import {
    login,
    clearMerchantIdCache,
    isOrganizationSelectionRequired,
    type LoginPrefill,
    type OrganizationChoice,
} from '@api/auth';
import { clearAllAiThreadCookies } from '../../../providers/AiAnalyst/useAiChatThreadPersistence';
import { clearAllAgentSessionStorage } from '@hooks/useAgentChat';
import { clearQuickHelpSessionStorage } from '@providers/QuickHelp/QuickHelpProvider';
import { getPasswordSchema, validateEmail } from '@diagon-alley/common';
import { useTranslate } from '@i18n';
import {
    Anchor,
    Button,
    PasswordInput,
    Radio,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

// ----------------------------------------------
// i18n/useTranslate.shim.ts
// ----------------------------------------------
/**
 * A tiny i18n hook that works in the canvas preview without any external deps.
 * If your host app defines `window.__useTranslate` (a function returning { t, ... }),
 * this shim will delegate to it. Otherwise, it falls back to the embedded EN strings below.
 */
export type TranslateFn = (key: string, vars?: Record<string, any>) => string;
export type TranslateHook = () => {
    t: TranslateFn;
    i18n?: unknown;
    changeLanguage?: (lng: string) => void;
    currentLanguage?: string;
};

type DeepKey = string;
function getByPath(obj: any, path: string): any {
    return path
        .split('.')
        .reduce(
            (acc, p) => (acc && acc[p] !== undefined ? acc[p] : undefined),
            obj,
        );
}
function interpolate(str: string, vars?: Record<string, any>) {
    if (!vars) return str;
    return str.replace(/{{\s*(\w+)\s*}}/g, (_, k) => vars[k] ?? '');
}

// ----------------------------------------------
// hooks/useDebouncedBusy.ts
// ----------------------------------------------
/**
 * Shows `busy=true` only if an async action exceeds the given threshold.
 * Smooths out janky loaders for super-fast requests.
 */
export function useDebouncedBusy(thresholdMs = 400) {
    const [busy, setBusy] = useState(false);
    const timerRef = useRef<number | null>(null);

    const start = useCallback(() => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setBusy(true), thresholdMs);
    }, [thresholdMs]);

    const stop = useCallback(() => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = null;
        setBusy(false);
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
            }
        };
    }, []);
    return { busy, start, stop };
}

// ----------------------------------------------
// pages/LoginPage.tsx
// ----------------------------------------------
/**
 * Firecrawl-style login page using Mantine.
 * - Links to Forgot password & Invitation routes
 * - "Login with mock data" helper for quick testing
 */
// ----------------------------------------------
// pages/LoginPage.tsx
// ---------------------------------------------
// small, UI-only strength scorer (does NOT block login)
const scorePassword = (pw: string) => {
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const labels = [
        'Very weak',
        'Weak',
        'Okay',
        'Strong',
        'Very strong',
    ] as const;
    const colors = ['red', 'red', 'yellow', 'green', 'green'] as const;
    return {
        score, // 0..4
        label: labels[score],
        color: colors[score],
        percent: (score / 4) * 100,
    };
};

import { AuthContainer } from '../AuthContainer';

export const LoginPage: FC = () => {
    const { t } = useTranslate();
    const nav = useNavigate();
    const queryClient = useQueryClient();
    /** Signup sends people here with their address (and chosen org) already known. */
    const prefill = (useLocation().state ?? {}) as LoginPrefill;

    const [email, setEmail] = useState(prefill.email ?? '');
    const [password, setPassword] = useState('');

    // field-level errors
    const [emailErr, setEmailErr] = useState<string | null>(null);
    const [passErr, setPassErr] = useState<string | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const { busy, start, stop } = useDebouncedBusy(400);
    const emailRef = useRef<HTMLInputElement | null>(null);

    /**
     * Set when the credentials opened logins in more than one organisation. The
     * password is kept in state and resent with the pick — the server issues no
     * token until it knows which login to open.
     */
    const [organizations, setOrganizations] = useState<
        OrganizationChoice[] | null
    >(null);
    const [organizationId, setOrganizationId] = useState<string | null>(
        prefill.organizationId ?? null,
    );

    const handleSubmit = useCallback(async () => {
        // inline validate email only (do not block on password strength here)
        setEmailErr(null);
        setPassErr(null);

        if (!validateEmail(email)) {
            setEmailErr(t('login.errors.email') ?? 'Enter a valid email');
            return;
        }

        if (organizations && !organizationId) {
            setPassErr('Choose an organisation to continue');
            return;
        }

        start();
        setSubmitting(true);
        try {
            const res = await login({
                email,
                password,
                organizationId: organizationId ?? undefined,
            });

            if (isOrganizationSelectionRequired(res)) {
                setOrganizations(res.organizations);
                setOrganizationId(res.organizations[0]?.id ?? null);
                return;
            }

            // Clear ALL previous session data first
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            clearMerchantIdCache();
            clearAllAiThreadCookies();
            clearAllAgentSessionStorage();
            clearQuickHelpSessionStorage();

            // Store new credentials
            localStorage.setItem('token', res.accessToken.token);
            localStorage.setItem('user', JSON.stringify(res.user));
            
            // Force a full page reload to ensure clean state
            // This guarantees no stale React state from previous user
            window.location.href = '/';
        } catch (error: any) {
            setPassErr(
                error?.error?.message ||
                    error?.message ||
                    t('login.errors.generic'),
            );
        } finally {
            stop();
            setSubmitting(false);
        }
    }, [email, password, organizations, organizationId, nav, start, stop, t, queryClient]);

    const strength = scorePassword(password);
    const strongEnough = getPasswordSchema().safeParse(password).success;

    return (
        <AuthContainer subtitle="Welcome back">
            <Stack gap="md">
                <Stack gap="xs">
                    <Text size="sm" fw={500}>
                        Email
                    </Text>
                    <TextInput
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.currentTarget.value);
                            if (emailErr) setEmailErr(null);
                            // A different address means a different set of orgs.
                            setOrganizations(null);
                            setOrganizationId(null);
                        }}
                        ref={emailRef as any}
                        autoComplete="email"
                        error={emailErr ?? undefined}
                    />
                </Stack>

                <Stack gap="xs">
                    <Text size="sm" fw={500}>
                        Password
                    </Text>
                    <PasswordInput
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.currentTarget.value);
                            if (passErr) setPassErr(null);
                        }}
                        autoComplete="current-password"
                        error={passErr ?? undefined}
                    />
                </Stack>

                {organizations && (
                    <Radio.Group
                        label="This email belongs to more than one organisation"
                        description="Choose the one to open"
                        value={organizationId ?? ''}
                        onChange={(value) => {
                            setOrganizationId(value);
                            if (passErr) setPassErr(null);
                        }}
                    >
                        <Stack gap="xs" mt="xs">
                            {organizations.map((org) => (
                                <Radio
                                    key={org.id}
                                    value={org.id}
                                    label={org.name}
                                />
                            ))}
                        </Stack>
                    </Radio.Group>
                )}

                <Button
                    onClick={handleSubmit}
                    disabled={submitting || busy}
                    radius="md"
                    fullWidth
                    h={44}
                    style={{ backgroundColor: 'black', color: 'white' }}
                >
                    {submitting || busy
                        ? 'Logging in...'
                        : organizations
                          ? 'Continue'
                          : 'Login'}
                </Button>

                <div className="flex justify-center">
                    <Anchor
                        onClick={() => nav('/forgot-password')}
                        className="cursor-pointer"
                        size="sm"
                        c="dimmed"
                    >
                        {t('login.forgotPassword')}
                    </Anchor>
                </div>
            </Stack>
        </AuthContainer>
    );
};
